import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { LEARN_ESCANDALLO_EXCEL_VIEW_URL } from '@/features/aprender/learnArticleShared'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'

function TipiLink({ children = 'Tipi' }: { children?: ReactNode }) {
    return (
        <LocaleLink to='/' className={articleUi.link}>
            {children}
        </LocaleLink>
    )
}

export function LearnArticleCta() {
    const { t } = useTranslation()
    const whatsappHref = toOfficialSalesWhatsAppHref(
        t(
            'learn.articleCta.whatsappMessage',
            'Hola, me gustaría recibir una copia editable gratuita del Excel de escandallos de Tipi.',
        ),
    )

    return (
        <section className={articleUi.cta}>
            <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                {t('learn.articleCta.title', '¿Quieres empezar a controlar cuánto te cuesta realmente cada plato?')}
            </h2>
            <p className={articleUi.ctaSub}>{t('learn.articleCta.subtitle', 'Ahora tienes dos opciones.')}</p>

            <div className={cn(articleUi.ctaGrid, 'grid-cols-1 md:grid-cols-2')}>
                <div className={articleUi.ctaCard}>
                    <p className={articleUi.ctaEyebrow}>{t('learn.articleCta.option1.eyebrow', 'Opción 1')}</p>
                    <h3>{t('learn.articleCta.option1.title', 'Empieza gratis con nuestro Excel de escandallos')}</h3>
                    <p>
                        {t(
                            'learn.articleCta.option1.p1',
                            'Hemos preparado una plantilla sencilla para que puedas empezar a calcular el coste de tus platos, revisar tus márgenes y dejar de poner precios a ojo.',
                        )}
                    </p>
                    <p>
                        {t(
                            'learn.articleCta.option1.p2',
                            'Puedes acceder al Excel en modo lectura para ver cómo funciona.',
                        )}
                    </p>
                    <p>
                        {t(
                            'learn.articleCta.option1.p3',
                            'Si quieres recibir una copia editable totalmente gratuita, ponte en contacto con nosotros y te la enviaremos para que puedas utilizarla en tu negocio.',
                        )}
                    </p>
                    <div className={cn(articleUi.ctaActions, 'flex-col sm:flex-row')}>
                        <a
                            href={LEARN_ESCANDALLO_EXCEL_VIEW_URL}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(articleUi.btn, articleUi.btnSolid, 'w-full sm:w-auto')}
                        >
                            {t('learn.articleCta.option1.viewExcel', 'Ver el Excel gratuito')}
                        </a>
                        <a
                            href={whatsappHref}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(articleUi.btn, articleUi.btnOutline, 'w-full sm:w-auto')}
                        >
                            {t('learn.articleCta.option1.wantCopy', 'Quiero mi copia editable')}
                        </a>
                    </div>
                </div>

                <div className={articleUi.ctaCardDark}>
                    <p className={articleUi.ctaEyebrow}>{t('learn.articleCta.option2.eyebrow', 'Opción 2')}</p>
                    <h3>{t('learn.articleCta.option2.title', 'Hazlo de forma más sencilla con Tipi')}</h3>
                    <p>
                        {t(
                            'learn.articleCta.option2.p1Before',
                            'Si no quieres depender de hojas de cálculo ni actualizar manualmente cada precio, puedes hacerlo directamente desde',
                        )}{' '}
                        <TipiLink />
                        {t('learn.articleCta.option2.p1After', '.')}
                    </p>
                    <p>
                        {t(
                            'learn.articleCta.option2.p2',
                            'Centraliza tus recetas, escandallos, compras, albaranes y precios de proveedores para conocer cuánto te cuesta cada plato y detectar las subidas que están reduciendo tu margen.',
                        )}
                    </p>
                    <div className={cn(articleUi.ctaActions, 'flex-col sm:flex-row')}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid, 'w-full sm:w-auto')}>
                            {t('learn.articleCta.option2.cta', 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                </div>
            </div>

            <p className={articleUi.ctaClose}>
                {t('learn.articleCta.close1', 'Puedes seguir calculando a ojo y esperar que los números cuadren.')}
                <br />
                {t(
                    'learn.articleCta.close2',
                    'O puedes empezar hoy a saber cuánto ganas realmente cada vez que un plato sale de cocina.',
                )}
            </p>
        </section>
    )
}
